import { Box, Stack } from "@mui/material";
import {
  type DocumentLifecycleView,
  evaluateDocumentValidity,
  getDocumentValidityState,
} from "@repo/types";
import { isImageFile, isPdfFile } from "@repo/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  useApproveDocumentMutation,
  useGetDriverByIdQuery,
  useRejectDocumentMutation,
} from "@/libs/generated/hooks";
import type { ReasonObject } from "@/libs/generated/types";
import { DocumentListItemText } from "./DocumentListItemText";
import { DocumentPreview } from "./DocumentPreview";
import { DocumentReviewPanel } from "./DocumentReviewPanel";
import type { ReviewItemProps } from "./types";
import { UploadActions } from "./UploadActions";
import { UploadListItem } from "./UploadContent";
import { UploadPreview } from "./UploadPreview";
import { UploadProgress } from "./UploadProgress";
import { useDocumentRow } from "./use-document-row";

/** One uploaded file: its preview beside its dates and review decision. */
export function ReviewItem({ value, meta, handleDelete, handleFileUploaded }: ReviewItemProps) {
  const {
    isMobile,
    openPreview,
    setOpenPreview,
    isImage,
    isPDF,
    loading,
    uploadProgress,
    documentTitle,
    hasDocument,
    updatedAt,
    isDragDropEnabled,
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    actions,
    documentUrl,
    statusResult,
  } = useDocumentRow({
    value,
    meta,
    mode: "review",
    canEdit: true,
    onFileUploaded: handleFileUploaded,
  });

  const versionId = Number(value.docId ?? value.id);
  const isPending = value.version_status === "PENDING";
  // A pending or rejected version is only reachable through its own upload URL.
  const previewUrl =
    isPending || value.version_status === "REJECTED"
      ? value.tempObjectUrl
      : documentUrl || value.tempObjectUrl;
  const previewFile = { type: value.type, tempObjectUrl: previewUrl ?? "" };
  const previewIsImage = isImage || isImageFile(previewFile);
  const previewIsPDF = isPDF || isPdfFile(previewFile);

  const queryClient = useQueryClient();
  const approveDocument = useApproveDocumentMutation();
  const rejectDocument = useRejectDocumentMutation();
  const refreshDriver = () =>
    meta.driverId
      ? queryClient.invalidateQueries({
          queryKey: useGetDriverByIdQuery.getKey({ GetByIdInput: { id: Number(meta.driverId) } }),
        })
      : Promise.resolve();

  const datesChanged = (result: DocumentLifecycleView) => {
    handleFileUploaded?.({
      ...value,
      expiresAt: result.expiresAt,
      updatedAt: result.updatedAt,
      renewalOpen:
        result.isCurrent &&
        evaluateDocumentValidity(result.name, result.expiresAt, new Date()).renewalOpen,
      document_state: getDocumentValidityState(result.expiresAt, new Date()),
    });
  };

  const approve = async () => {
    await approveDocument.mutateAsync({ id: versionId });
    await refreshDriver();
    handleFileUploaded?.({
      ...value,
      url: null,
      tempObjectUrl: value.tempObjectUrl ?? value.url,
      version_status: "APPROVED",
      document_state: "VALID",
    });
  };

  const reject = async (reason: ReasonObject) => {
    await rejectDocument.mutateAsync({ CancelDocumentInput: { id: versionId, reason } });
    await refreshDriver();
    const hadApprovedDoc = Boolean(documentUrl || value.url);
    handleFileUploaded?.({
      ...value,
      url: hadApprovedDoc ? value.url || documentUrl : null,
      tempObjectUrl: value.tempObjectUrl ?? value.url,
      version_status: hadApprovedDoc ? "APPROVED" : "REJECTED",
      document_state: hadApprovedDoc ? (value.document_state ?? "VALID") : "MISSING",
      rejectionReason: reason.reason ?? null,
      additionalInfo: reason.additionalInfo ?? null,
    });
  };

  const actionError = [approveDocument.error, rejectDocument.error].find(
    (error): error is Error => error instanceof Error
  )?.message;

  return (
    <>
      <UploadListItem
        {...getRootProps()}
        hasDocument={hasDocument}
        hasPendingReview={isPending}
        isDragActive={isDragActive}
        isDragDropEnabled={isDragDropEnabled}
        loading={loading}
        component="div"
        sx={{ height: "auto", alignItems: "stretch", p: 2 }}
      >
        <input {...getInputProps()} style={{ display: "none" }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: "100%" }}>
          {previewUrl && (
            <Box
              sx={{
                width: 176,
                maxWidth: "100%",
                alignSelf: "flex-start",
                flexShrink: 0,
                bgcolor: "grey.50",
                borderRadius: 1,
                p: 1,
              }}
            >
              <DocumentPreview
                url={previewUrl}
                title={documentTitle}
                isImage={previewIsImage}
                isPDF={previewIsPDF}
                onOpen={hasDocument ? () => setOpenPreview(true) : undefined}
              />
            </Box>
          )}

          <Stack
            spacing={1.5}
            sx={{ flex: 1, minWidth: 0, "& > .MuiListItemText-root": { flex: "0 0 auto" } }}
          >
            <DocumentListItemText
              documentTitle={documentTitle}
              documentName={value.name ?? value.label ?? ""}
              isImage={previewIsImage}
              isPDF={previewIsPDF}
              updatedAt={updatedAt}
              versionStatus={value.version_status ?? undefined}
              documentState={value.document_state}
              rejectionReason={value.rejectionReason ?? undefined}
              additionalInfo={value.additionalInfo ?? undefined}
              handledBy={value.handledBy}
              expiresAt={value.expiresAt}
              statusOverride={statusResult.status}
              showValidity={false}
            />

            {loading && (
              <Box sx={{ width: isMobile ? "100%" : 200 }}>
                <UploadProgress loading={loading} value={uploadProgress} />
              </Box>
            )}

            <Box>
              <UploadActions
                canChange={actions.canChange}
                canDelete={actions.canDelete}
                canOpen={actions.canOpen || Boolean(previewUrl)}
                canDownload={actions.canDownload || Boolean(previewUrl)}
                id={value.key}
                loading={loading}
                onChange={open}
                onDelete={() => handleDelete?.(value.key)}
                onDownload={async () => {
                  const urlToDownload = previewUrl || documentUrl || actions.documentUrl;
                  if (!urlToDownload) return;

                  try {
                    // Fetch as blob to preserve correct file type
                    const response = await fetch(urlToDownload);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = documentTitle;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error("Download failed:", error);
                    // Fallback to direct download
                    const link = document.createElement("a");
                    link.href = urlToDownload;
                    link.download = documentTitle;
                    link.click();
                  }
                }}
                onOpen={() => setOpenPreview(true)}
              />
            </Box>

            {meta.driverId && versionId > 0 && (
              <DocumentReviewPanel
                versionId={versionId}
                isPending={isPending}
                onApprove={approve}
                onReject={reject}
                onChanged={datesChanged}
                actionError={actionError}
              />
            )}
          </Stack>
        </Stack>
      </UploadListItem>

      {(hasDocument || Boolean(previewUrl)) && !loading && (
        <UploadPreview
          document={{ ...value, url: previewUrl || documentUrl }}
          isImage={previewIsImage}
          onClose={() => setOpenPreview(false)}
          open={openPreview}
        />
      )}
    </>
  );
}
